import { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Table, MagnifyingGlass, ArrowsClockwise, Tag,
  ShieldCheck, Star, Link as LinkIcon, ChatCircle,
  PaperPlaneRight, Funnel, Database
} from '@phosphor-icons/react';

const TYPE_COLORS = {
  string: 'kova-cyan', number: 'kova-purple', boolean: 'kova-green',
  object: 'kova-amber', array: 'kova-gold', date: 'kova-silv',
  enum: 'kova-red', unknown: 'kova-slate',
};

export default function DataDictionaryPage() {
  const [stats, setStats] = useState(null);
  const [fields, setFields] = useState([]);
  const [fieldsTotal, setFieldsTotal] = useState(0);
  const [concepts, setConcepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [filterConn, setFilterConn] = useState('');
  const [filterType, setFilterType] = useState('');
  const [tab, setTab] = useState('fields');
  const [showNL, setShowNL] = useState(false);
  const [showNewConcept, setShowNewConcept] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterConn) params.set('connection_id', filterConn);
      if (filterType) params.set('field_type', filterType);
      params.set('limit', '100');

      const [fieldsRes, conceptsRes, statsRes] = await Promise.all([
        fetch(`${API}/dictionary/fields?${params}`, { credentials: 'include' }),
        fetch(`${API}/dictionary/concepts`, { credentials: 'include' }),
        fetch(`${API}/dictionary/stats`, { credentials: 'include' }),
      ]);
      if (fieldsRes.ok) { const d = await fieldsRes.json(); setFields(d.fields || []); setFieldsTotal(d.total || 0); }
      if (conceptsRes.ok) setConcepts(await conceptsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (e) { console.error('Dictionary fetch error:', e); }
    setLoading(false);
  }, [search, filterConn, filterType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/dictionary/catalog/sync`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        toast.success(`Synced ${d.synced_new_fields} new fields from ${d.connections_scanned} connections`);
        fetchData();
      }
    } catch { toast.error('Sync failed'); }
    setSyncing(false);
  };

  return (
    <div className="space-y-6" data-testid="data-dictionary-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-kova-white tracking-tight">Data Dictionary</h1>
          <p className="font-mono text-[10px] tracking-[2px] uppercase text-kova-slate mt-1">F-014 Cross-System Field Intelligence</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowNL(true)} data-testid="nl-query-btn" className="flex items-center gap-2 font-mono text-[10px] tracking-[2px] uppercase text-kova-silv border border-kova-cyan/12 px-3 py-2 hover:border-kova-cyan/25 hover:text-kova-white transition-colors">
            <ChatCircle size={14} /> Ask
          </button>
          <button onClick={handleSync} disabled={syncing} data-testid="sync-btn" className="flex items-center gap-2 font-mono text-[10px] tracking-[2px] uppercase bg-kova-cyan text-kova-bg px-4 py-2 hover:bg-kova-cyan2 transition-colors disabled:opacity-50 cursor-crosshair">
            <ArrowsClockwise size={14} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Syncing...' : 'Sync Catalog'}
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-kova-cyan/12">
          {[
            { label: 'Total Fields', value: stats.total_fields },
            { label: 'PII Fields', value: stats.pii_fields, color: 'kova-red' },
            { label: 'Source of Truth', value: stats.sot_fields, color: 'kova-gold' },
            { label: 'Mapped', value: stats.mapped_fields, color: 'kova-green' },
            { label: 'Unmapped', value: stats.unmapped_fields, color: 'kova-amber' },
            { label: 'Concepts', value: stats.total_concepts, color: 'kova-purple' },
          ].map((s, i) => (
            <div key={i} className="bg-kova-card p-3">
              <p className={`font-heading text-lg font-bold ${s.color ? `text-${s.color}` : 'text-kova-white'}`}>{s.value}</p>
              <p className="font-mono text-[9px] tracking-[1px] uppercase text-kova-slate mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-0 border-b border-kova-cyan/12">
        {['fields', 'concepts'].map(t => (
          <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)} className={`px-4 py-2 font-mono text-[10px] tracking-[2px] uppercase ${tab === t ? 'text-kova-cyan border-b-2 border-kova-cyan' : 'text-kova-slate hover:text-kova-silv'}`}>
            {t === 'fields' ? `Fields (${fieldsTotal})` : `Concepts (${concepts.length})`}
          </button>
        ))}
      </div>

      {tab === 'fields' ? (
        <>
          {/* Search & Filters */}
          <div className="flex gap-3 items-center flex-wrap">
            <div className="relative flex-1 max-w-sm">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-kova-slate" />
              <input data-testid="dict-search" type="text" placeholder="Search fields..." value={search} onChange={e => setSearch(e.target.value)} className="w-full bg-kova-card border border-kova-cyan/12 text-kova-white font-mono text-[10px] pl-8 pr-3 py-1.5 focus:outline-none focus:border-kova-cyan placeholder:text-kova-slate" />
            </div>
            <select data-testid="filter-type" value={filterType} onChange={e => setFilterType(e.target.value)} className="bg-kova-card border border-kova-cyan/12 text-kova-silv font-mono text-[10px] px-3 py-1.5">
              <option value="">All Types</option>
              {Object.keys(TYPE_COLORS).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {(search || filterConn || filterType) && (
              <button onClick={() => { setSearch(''); setFilterConn(''); setFilterType(''); }} className="text-kova-slate hover:text-kova-silv font-mono text-[9px]">Clear</button>
            )}
          </div>

          {/* Fields Table */}
          {loading ? (
            <div className="text-center py-12 text-kova-slate font-mono text-xs">Loading fields...</div>
          ) : fields.length === 0 ? (
            <div className="text-center py-12 border border-kova-cyan/12 bg-kova-card">
              <Table size={32} className="mx-auto mb-3 text-kova-slate" />
              <p className="text-kova-silv text-sm">{search ? 'No fields match your search' : 'No fields in dictionary. Click Sync Catalog to scan your schemas.'}</p>
            </div>
          ) : (
            <div className="border border-kova-cyan/12 overflow-x-auto">
              <div className="grid grid-cols-[1fr_120px_80px_60px_60px_1fr] bg-kova-card2 px-4 py-2 font-mono text-[9px] tracking-[1px] uppercase text-kova-slate min-w-[700px]">
                <span>Field Path</span><span>Source</span><span>Type</span><span>PII</span><span>SoT</span><span>Concept</span>
              </div>
              {fields.map(f => (
                <div key={f.field_id} className="grid grid-cols-[1fr_120px_80px_60px_60px_1fr] px-4 py-2 border-t border-kova-cyan/6 font-mono text-xs min-w-[700px] hover:bg-kova-card2/50">
                  <span className="text-kova-white truncate" title={f.field_path}>{f.field_path}</span>
                  <span className="text-kova-silv truncate">{f.source_name}</span>
                  <span className={`text-${TYPE_COLORS[f.field_type] || 'kova-slate'} text-[10px]`}>{f.field_type}</span>
                  <span>{f.is_pii ? <ShieldCheck size={12} weight="fill" className="text-kova-red" /> : <span className="text-kova-cyan/20">—</span>}</span>
                  <span>{f.is_source_of_truth ? <Star size={12} weight="fill" className="text-kova-gold" /> : <span className="text-kova-cyan/20">—</span>}</span>
                  <span className="text-kova-purple truncate text-[10px]">{concepts.find(c => c.concept_id === f.canonical_concept_id)?.name || ''}</span>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Concepts */}
          <div className="flex justify-end mb-2">
            <button onClick={() => setShowNewConcept(true)} data-testid="new-concept-btn" className="flex items-center gap-2 font-mono text-[10px] tracking-[2px] uppercase bg-kova-cyan text-kova-bg px-4 py-2 hover:bg-kova-cyan2 cursor-crosshair">
              <LinkIcon size={14} /> New Concept
            </button>
          </div>
          {concepts.length === 0 ? (
            <div className="text-center py-12 border border-kova-cyan/12 bg-kova-card">
              <LinkIcon size={32} className="mx-auto mb-3 text-kova-slate" />
              <p className="text-kova-silv text-sm">No canonical concepts defined yet</p>
            </div>
          ) : (
            <div className="space-y-1">
              {concepts.map(c => (
                <div key={c.concept_id} className="bg-kova-card border border-kova-cyan/12 p-4 hover:border-kova-cyan/25 transition-colors" data-testid={`concept-${c.concept_id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-heading text-sm font-bold text-kova-white">{c.name}</h3>
                      {c.description && <p className="text-kova-silv text-xs mt-0.5">{c.description}</p>}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-[9px] text-kova-slate">{c.data_type}</span>
                      <span className="font-mono text-[10px] text-kova-purple">{c.mapped_field_count} field{c.mapped_field_count !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* NL Query Modal */}
      <AnimatePresence>
        {showNL && <NLQueryModal onClose={() => setShowNL(false)} />}
        {showNewConcept && <NewConceptModal onClose={() => setShowNewConcept(false)} onSuccess={() => { setShowNewConcept(false); fetchData(); }} />}
      </AnimatePresence>
    </div>
  );
}

function NLQueryModal({ onClose }) {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleAsk = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/dictionary/query`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question }),
      });
      if (res.ok) setAnswer(await res.json());
    } catch { toast.error('Query failed'); }
    setLoading(false);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-kova-card border border-kova-cyan/12 p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="nl-query-modal">
        <h2 className="font-heading text-lg font-bold text-kova-white mb-4">Ask the Dictionary</h2>
        <form onSubmit={handleAsk} className="space-y-4">
          <input data-testid="nl-question" type="text" value={question} onChange={e => setQuestion(e.target.value)} placeholder="e.g. Which fields store email addresses?" className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2 placeholder:text-kova-slate focus:outline-none focus:border-kova-cyan" />
          <button data-testid="nl-ask-btn" type="submit" disabled={loading} className="font-mono text-[10px] tracking-[2px] uppercase bg-kova-cyan text-kova-bg px-4 py-2 hover:bg-kova-cyan2 disabled:opacity-50">{loading ? 'Thinking...' : 'Ask'}</button>
        </form>
        {answer && (
          <div className="mt-4 p-4 bg-kova-bg border border-kova-cyan/12">
            <p className="text-kova-white text-sm leading-relaxed whitespace-pre-wrap">{answer.answer}</p>
            <p className="text-kova-slate font-mono text-[9px] mt-2">{answer.fields_in_context} fields, {answer.concepts_in_context} concepts in context</p>
          </div>
        )}
        <button onClick={onClose} className="mt-4 font-mono text-[10px] text-kova-slate hover:text-kova-silv">Close</button>
      </motion.div>
    </motion.div>
  );
}

function NewConceptModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({ name: '', description: '', data_type: 'string' });
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/dictionary/concepts`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) { toast.success('Concept created'); onSuccess(); }
      else { const d = await res.json(); toast.error(d.detail || 'Failed'); }
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-kova-card border border-kova-cyan/12 p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="new-concept-modal">
        <h2 className="font-heading text-lg font-bold text-kova-white mb-4">New Canonical Concept</h2>
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Name</label>
            <input data-testid="concept-name" type="text" required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. customer_email" className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2 placeholder:text-kova-slate focus:outline-none focus:border-kova-cyan" />
          </div>
          <div>
            <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Description</label>
            <input type="text" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2 focus:outline-none focus:border-kova-cyan" />
          </div>
          <div>
            <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Data Type</label>
            <select value={form.data_type} onChange={e => setForm(p => ({ ...p, data_type: e.target.value }))} className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2">
              {['string','number','boolean','date','object','array','enum'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="font-mono text-[10px] tracking-[2px] uppercase text-kova-silv border border-kova-cyan/12 px-4 py-2 hover:border-kova-cyan/25">Cancel</button>
            <button data-testid="concept-save" type="submit" disabled={saving} className="font-mono text-[10px] tracking-[2px] uppercase bg-kova-cyan text-kova-bg px-4 py-2 hover:bg-kova-cyan2 disabled:opacity-50">{saving ? 'Saving...' : 'Create'}</button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
